import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { SlIconComponent, SlMenuComponent } from '@sl/sl-angular-library';
import { RoleService } from 'src/app/core/services/role.service';
import { environment } from 'src/environments/environment';
import { FeatureFlagsState } from '../../feature-flag/communication/feature-flag.state';
import { RoleGroup } from '../../roles/models/role-group';
import { ERole } from '../../roles/models/role.enum';
import { MenuItem } from './menu-item.model';
import { CourseService } from 'src/app/core/services/course.service';
import { PeopleService } from 'src/app/core/services/people.service';
import { switchMap } from 'rxjs';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss', 'header.component.mobile.scss'],
  imports: [FormsModule, CommonModule, SlIconComponent, RouterModule, SlMenuComponent]
})

export class HeaderComponent implements OnInit{

  public menu: Array<MenuItem>;
  public menuOpen=false;
  public menuNewOpen=false;

  constructor(
    private roleService: RoleService, 
    private router: Router,
    private featureFlagsState: FeatureFlagsState,
    private peopleService: PeopleService,
    private courseService: CourseService
  ) {
    this.menu = [
      new MenuItem('Student', '/student/cases', '', false, true),
      new MenuItem('Impersonate', '/impersonate', '', false,this.hasAnyRole([ERole.SWITCH_USER])),
      new MenuItem('Incident Queue', '/incident-queue/active', '{reload: true}', false, this.hasAnyRole(RoleGroup.NON_INCIDENT_QUEUES)),
      new MenuItem('Search', '/search', '', false, this.hasAnyRole([ERole.SEARCH])),
      new MenuItem('Config', '/config', '', false, this.hasAnyRole([ERole.MAINTENANCE])),
      new MenuItem('New', '/new', '', false, true, undefined, [
        new MenuItem('Student Complaint', '/new/student-complaint', '', false, this.hasAnyRole(RoleGroup.STUDENT_COMPLAINT)),
        new MenuItem('School of Law Informational IR', '/new/school-of-law-informational-ir', '', false, this.hasAnyRole([ERole.GROUP_LAW_INFORMATIONAL])),
        new MenuItem('School of Law Violations', '/new/school-of-law-violations', '', false, this.hasAnyRole([ERole.GROUP_LAW_VIOLATIONS])),
        new MenuItem('Informational IR', '/new/informational-ir', '', false, this.hasAnyRole([ERole.GROUP_DOS_INFORMATIONAL, ERole.DOS_INFORMATIONAL_SUBMIT])),
        new MenuItem('Counseling IR', '/new/counseling-ir', '', false, false),
        new MenuItem('Spiritual Accounting IR', '/new/spiritual-accounting', '', false, this.hasAnyRole([ERole.GROUP_SPIRITUAL_ACCOUNTING])),
        new MenuItem('Final Grade Appeal', '/new/grade-appeal', '', false, this.hasAnyRole(RoleGroup.STUDENT_APPEALS)),
        new MenuItem('Academic Misconduct IR', '/new/honor-code', '', false, this.hasAnyRole([ERole.GROUP_HONOR_CODE, ERole.OGSA_FACULTY])),
        new MenuItem('LUOA Honor Code IR', '/new/luoa-honor-code', '', false, this.hasAnyRole([ERole.GROUP_LUOA_HONOR_CODE, ERole.LUOA_FACULTY])),
        new MenuItem('LUOA Clubs IR', '/new/luoa-clubs', '', false, this.hasAnyRole([ERole.GROUP_LUOA_CLUBS, ERole.LUOA_FACULTY])),
        new MenuItem('LUOA Extension', '/new/luoa-extensions', '', false, this.hasAnyRole([ERole.LUOA_EXTENSIONS, ERole.LUOA_EXTENSIONS_SUBMIT, ERole.LUOA_PROXY])),
        new MenuItem('Ombudsman Complaint', '/new/ombudsman-complaint', '', false, this.hasAnyRole([ERole.GROUP_OMBUDSMAN])),
        new MenuItem('Admissions Appeal', '/new/admissions-appeal', '', false, true),
        new MenuItem('Scholarship Appeal', '/new/scholarship-appeal', '', false, this.hasAnyRole(RoleGroup.SCHOLARSHIP_APPEALS)),
        new MenuItem('CARE Referral', '/new/care-referral', '', false, true),
        new MenuItem('Sexual Misconduct Report', 'https://cm.maxient.com/reportingform.php?LibertyUniv&layout_id=1', '', true, true),
        new MenuItem('Harassment/Discrimination Report', 'https://cm.maxient.com/reportingform.php?LibertyUniv&layout_id=1', '', true, true)
      ]),
      new MenuItem('Logout', `${environment.apiUrl}/logout`, '', true, true, undefined, undefined)
    ];
  }

  ngOnInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if(this.menuOpen){
          this.showMenu();
        }
        if(this.menuNewOpen){
          this.showNewMenu();
        } 
      }
    });

    this.featureFlagsState.getFlags().subscribe(response => {
      let newMenuItem: MenuItem | null = null;

      if(response && this.menu[5].submenu) {
        const libertyWayIndex = this.menu[5].submenu.findIndex(item => 
          item.linkText.includes('Informational IR')
        );

        if(response.includes('LIBERTY_WAY_OLD')) {
          newMenuItem = new MenuItem('Liberty Way Violation IR', '/new/liberty-way-ir', '', false, this.hasAnyRole([ERole.IR_SUBMIT]));
          this.menu[5].submenu.splice(libertyWayIndex, 0, newMenuItem);
        } else if(response.includes('LIBERTY_WAY_NEW')) {
          newMenuItem = new MenuItem('Liberty Way Violation IR', '/new/new-liberty-way-ir', '', false, this.hasAnyRole([ERole.IR_SUBMIT]));
          this.menu[5].submenu.splice(libertyWayIndex, 0, newMenuItem);
        }

        const luoaAppealIndex = this.menu[5].submenu.findIndex(item => 
          item.linkText.includes('LUOA Extension')
        );
        
        if(response.includes('LUOA_FN_APPEAL')) {  
          newMenuItem = new MenuItem('LUOA FN Appeal', '/new/luoa-appeals', '', false, this.hasAnyRole([ERole.LUOA_APPEALS, ERole.LUOA_APPEALS_SUBMIT, ERole.LUOA_PROXY]));
          this.menu[5].submenu.splice(luoaAppealIndex, 0, newMenuItem);
        }

        const scholarchipAppealIndex = this.menu[5].submenu.findIndex(item => 
          item.linkText.includes('Scholarship Appeal')
        );

        if(response.includes('DOCTORAL_DISMISSAL_APPEAL')) {
          this.insertProgramDismissalAppealMenuItem(this.menu[5], scholarchipAppealIndex + 1);
        }

        const careReferralIndex = this.menu[5].submenu.findIndex(item => 
          item.linkText.includes('CARE Referral')
        );

        if (response.includes('CSER_GRADE_APPEAL')) {
          if (this.hasAnyRole([ERole.GROUP_CSER_GRADE_APPEALS])) {
            this.insertCserGradeAppealMenuItem(this.menu[5], careReferralIndex + 1);
          } else {
            this.peopleService.getCurrentUser().subscribe(currentUser => {
              this.courseService.getStudentCserAppealEligibleCourses(currentUser.pidm).subscribe((courses) => {
                if (courses && courses.length > 0) {
                  this.insertCserGradeAppealMenuItem(this.menu[5], careReferralIndex + 1);
                }
              });
            });
          }
        }
      } 
    });
  }

  public showMenu():void{
    this.menuOpen = !this.menuOpen;
  }

  public showNewMenu():void{
    this.menuNewOpen = !this.menuNewOpen;
  }

  private hasAnyRole(role: Array<ERole>) {
    return this.roleService.hasAnyRole(role);
  }

  private insertCserGradeAppealMenuItem(menu: MenuItem, position: number) {
    const newMenuItem = new MenuItem('CSER Grade Appeal', '/new/cser-grade-appeal', '', false, this.hasAnyRole(RoleGroup.CSER_GRADE_APPEALS));
    menu.submenu?.splice(position, 0, newMenuItem);
  }

  private insertProgramDismissalAppealMenuItem(menu: MenuItem, position: number) {
    if(this.roleService.hasRole(ERole.GROUP_COMPLAINTS_EX)) {
      const newMenuItem = new MenuItem('Program Dismissal Appeal', '/new/program-dismissal-appeal', '', false, true);
      menu.submenu?.splice(position, 0, newMenuItem);
    }else {
      this.peopleService.getCurrentUser().pipe(
        switchMap(currentUser => 
          this.peopleService.hasProgramDismissal(currentUser.pidm) 
        )
      ).subscribe(hasDismissal => {
        if(hasDismissal) {
          const newMenuItem = new MenuItem('Program Dismissal Appeal', '/new/program-dismissal-appeal', '', false, this.hasAnyRole(RoleGroup.STUDENT_COMPLAINT));
          menu.submenu?.splice(position, 0, newMenuItem);
        }
      });
    }
    
  }

}
